<template>
  <div class="gallery-container" ref="galleryContainer">
    <div class="gallery-grid" ref="galleryGrid">
      <div
        v-for="(item, index) in itemsToDisplay"
        :key="item.img"
        class="gallery-item"
      >
        <img :src="item.img" alt="Gallery Image" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, computed, watch } from "vue";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { gallery as galleryDataRaw } from "@/data/data";

gsap.registerPlugin(ScrollTrigger);

const galleryGrid = ref(null);
const galleryContainer = ref(null);
const breakpoint = 768;

const allItems = galleryDataRaw.slice(0, 6).map((item, index) => ({
  ...item,
  id: `gallery-${index}-${item.img}`,
}));

const isMobile = ref(false);
const stInstances = ref([]);

const checkViewport = () => {
  if (typeof window !== 'undefined') {
    isMobile.value = window.innerWidth < breakpoint;
  }
};

const itemsToDisplay = computed(() => {
  if (isMobile.value) {
    return allItems.slice(null);
  }
  return allItems;
});

const setupScrollOutAnimations = async () => {
  stInstances.value.forEach(st => st.kill());
  stInstances.value = [];

  await nextTick();

  if (!galleryGrid.value) return;
  const items = galleryGrid.value.querySelectorAll(".gallery-item");

  if (!items.length) return;

  items.forEach((itemElement, index) => {
    const moveDirection = index % 2 === 0 ? -1 : 1;
    const xPercentTarget = 120 * moveDirection;
    const rotationTarget = 15 * moveDirection;

    const anim = gsap.to(itemElement, {
      xPercent: xPercentTarget,
      rotation: rotationTarget,
      scale: 1.1,
      opacity: 0,
      ease: "power1.inOut",
      scrollTrigger: {
        trigger: itemElement,
        start: "center center",
        end: "bottom center",
        scrub: 1.5,
        invalidateOnRefresh: true,
      },
    });
    stInstances.value.push(anim.scrollTrigger);
  });
};

onMounted(() => {
  checkViewport();
  window.addEventListener("resize", checkViewport);
  
  gsap.delayedCall(0.1, () => {
    setupScrollOutAnimations();
  });

  watch(itemsToDisplay, () => {
    setupScrollOutAnimations();
  }, { deep: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkViewport);
  stInstances.value.forEach(st => st.kill());
  stInstances.value = [];
});
</script>

<style scoped lang="scss">
.gallery-container {
  width: 100%;
  height: 100%;
  padding: 0;
}

.gallery-grid {
  display: grid;
  width: 100%;
  height: 100%;
  @media (max-width: 767px) {
    width: 100%;
    height: 80%;
  }
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  @media (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
}

.gallery-item {
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 10px;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;

  img {
    width: 95%;
    height: 95%;
    object-fit: cover;
    display: block;
    border-radius: 6px;
  }
}

// Apply default rotation to items - alternating
.gallery-grid > .gallery-item:nth-child(odd) {
  transform: rotate(1.5deg);
}

.gallery-grid > .gallery-item:nth-child(even) {
  transform: rotate(-1.5deg);
}

</style>
